import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2" }
const _hoisted_3 = { class: "flex flex-col gap-6" }
const _hoisted_4 = { class: "bg-muted/40 border-b p-3 flex items-center justify-between gap-4" }
const _hoisted_5 = { class: "flex-1 flex items-center gap-2" }
const _hoisted_6 = ["onUpdate:modelValue"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "p-4 flex flex-col gap-4" }
const _hoisted_9 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_10 = { class: "flex items-center gap-2" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex flex-wrap gap-2 mt-1 ml-2" }
const _hoisted_14 = ["onUpdate:modelValue"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "mt-2 pl-4" }
const _hoisted_18 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Clock, Plus, Trash2, RefreshCw, Layers } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface TimelineEvent {
  label: string
}

interface TimelinePeriod {
  period: string
  events: TimelineEvent[]
}

interface TimelineSection {
  name: string
  periods: TimelinePeriod[]
}

// Estado visual

export default /*@__PURE__*/_defineComponent({
  __name: 'TimelineEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
const timelineTitle = ref('Evolución del Proyecto')
const sections = ref<TimelineSection[]>([
  {
    name: 'Fase Inicial',
    periods: [
      {
        period: 'Enero 2023',
        events: [{ label: 'Idea original' }, { label: 'Prototipo' }]
      }
    ]
  }
])

// Acciones
const addSection = () => {
  sections.value.push({
    name: `Nueva Sección ${sections.value.length + 1}`,
    periods: []
  })
}

const removeSection = (secIdx: number) => {
  sections.value.splice(secIdx, 1)
}

const addPeriod = (secIdx: number) => {
  sections.value[secIdx].periods.push({
    period: 'Nuevo Periodo',
    events: [{ label: 'Evento' }]
  })
}

const removePeriod = (secIdx: number, perIdx: number) => {
  sections.value[secIdx].periods.splice(perIdx, 1)
}

const addEvent = (secIdx: number, perIdx: number) => {
  sections.value[secIdx].periods[perIdx].events.push({ label: 'Nuevo Evento' })
}

const removeEvent = (secIdx: number, perIdx: number, evIdx: number) => {
  sections.value[secIdx].periods[perIdx].events.splice(evIdx, 1)
}

// Generar código desde el estado visual
const generateMermaid = () => {
  let code = `timeline\n`
  if (timelineTitle.value.trim()) {
    code += `    title ${timelineTitle.value.trim()}\n`
  }
  
  sections.value.forEach(section => {
    if (section.name.trim()) {
      code += `    section ${section.name.trim()}\n`
    }
    
    section.periods.forEach(period => {
      const p = period.period.trim() || 'Periodo'
      
      if (period.events.length > 0) {
        code += `        ${p}`
        period.events.forEach(event => {
            const evLabel = event.label.trim().replace(/:/g, '-') || 'Evento'
            code += ` : ${evLabel}`
        })
        code += '\n'
      } else {
        code += `        ${p}\n`
      }
    })
  })
  
  editableCode.value = code
}

// Sincronizar desde código a estado visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSections: TimelineSection[] = []
  let currentSection: TimelineSection = { name: '', periods: [] }
  let newTitle = ''
  
  for (const line of lines) {
    const tLine = line.trim()
    if (tLine.startsWith('timeline')) {
      continue
    } else if (tLine.startsWith('title ')) {
      newTitle = tLine.replace('title ', '').trim()
    } else if (tLine.startsWith('section ')) {
      if (currentSection.name || currentSection.periods.length > 0) {
        newSections.push(currentSection)
      }
      currentSection = { name: tLine.replace('section ', '').trim(), periods: [] }
    } else if (tLine.includes(':')) {
      const parts = tLine.split(':').map(s => s.trim())
      
      if (parts[0] === '' && currentSection.periods.length > 0) {
        // Evento que continúa del periodo anterior
        const lastPeriod = currentSection.periods[currentSection.periods.length - 1]
        for (let i = 1; i < parts.length; i++) {
          if (parts[i]) {
            lastPeriod.events.push({ label: parts[i] })
          }
        }
      } else if (parts[0] !== '') {
        // Nuevo periodo
        const p: TimelinePeriod = { period: parts[0], events: [] }
        for (let i = 1; i < parts.length; i++) {
          if (parts[i]) {
            p.events.push({ label: parts[i] })
          }
        }
        currentSection.periods.push(p)
      }
    }
  }
  
  if (currentSection.name || currentSection.periods.length > 0) {
    newSections.push(currentSection)
  }
  
  timelineTitle.value = newTitle
  if (newSections.length > 0) {
    sections.value = newSections
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([timelineTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Línea de Tiempo (Timeline)",
    description: "Crea líneas de tiempo históricas o proyecciones a futuro. Agrupa periodos y eventos en secciones.",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[3] || (_cache[3] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Clock), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        onClick: addSection
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
          _cache[4] || (_cache[4] = _createTextVNode(" Añadir Sección", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[5] || (_cache[5] = _createElementVNode("label", { class: "text-sm font-semibold" }, "Título Principal", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timelineTitle).value = $event)),
            class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium",
            placeholder: "Ej. Historia de la Inteligencia Artificial"
          }, null, 512), [
            [_vModelText, timelineTitle.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, secIdx) => {
            return (_openBlock(), _createElementBlock("div", {
              key: secIdx,
              class: "bg-card border rounded-lg shadow-sm flex flex-col overflow-hidden"
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Layers), { class: "w-4 h-4 text-muted-foreground" }),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((section.name) = $event),
                    class: "flex-1 bg-transparent text-sm font-bold focus:outline-none focus:border-b border-primary/50",
                    placeholder: "Nombre de la Sección (Opcional)"
                  }, null, 8, _hoisted_6), [
                    [_vModelText, section.name]
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeSection(secIdx)),
                  class: "text-muted-foreground hover:text-destructive transition-colors p-1",
                  title: "Eliminar Sección completa"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_7)
              ]),
              _createElementVNode("div", _hoisted_8, [
                (section.periods.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Sin periodos en esta sección. "))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.periods, (period, perIdx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: perIdx,
                    class: "flex flex-col gap-2 relative pl-4 border-l-2 border-primary/30"
                  }, [
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm" }, [
                      _createElementVNode("div", { class: "w-2 h-2 rounded-full bg-background" })
                    ], -1)),
                    _createElementVNode("div", _hoisted_10, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((period.period) = $event),
                        class: "font-bold text-sm bg-background border rounded px-2 py-1 w-32 focus:outline-none focus:ring-1 focus:ring-primary shadow-sm",
                        placeholder: "Ej. 2024"
                      }, null, 8, _hoisted_11), [
                        [_vModelText, period.period]
                      ]),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (removePeriod(secIdx, perIdx)),
                        class: "text-muted-foreground hover:text-destructive opacity-50 hover:opacity-100 transition-opacity p-1"
                      }, [
                        _createVNode(_unref(Trash2), { class: "w-3 h-3" })
                      ], 8, _hoisted_12)
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(period.events, (event, evIdx) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: evIdx,
                          class: "flex items-center bg-muted border rounded-full px-3 py-1 shadow-sm group"
                        }, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": ($event: any) => ((event.label) = $event),
                            class: "bg-transparent text-xs focus:outline-none text-foreground font-medium w-auto min-w-[60px]",
                            placeholder: "Nombre del evento"
                          }, null, 8, _hoisted_14), [
                            [_vModelText, event.label]
                          ]),
                          _createElementVNode("button", {
                            onClick: ($event: any) => (removeEvent(secIdx, perIdx, evIdx)),
                            class: "ml-2 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                          }, [
                            _createVNode(_unref(Trash2), { class: "w-3 h-3" })
                          ], 8, _hoisted_15)
                        ]))
                      }), 128)),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (addEvent(secIdx, perIdx)),
                        class: "flex items-center bg-background border border-dashed hover:border-primary hover:text-primary rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors"
                      }, [
                        _createVNode(_unref(Plus), { class: "w-3 h-3 mr-1" }),
                        _cache[6] || (_cache[6] = _createTextVNode(" Evento ", -1))
                      ], 8, _hoisted_16)
                    ])
                  ]))
                }), 128)),
                _createElementVNode("div", _hoisted_17, [
                  _createVNode(_unref(Button), {
                    variant: "secondary",
                    size: "sm",
                    onClick: ($event: any) => (addPeriod(secIdx)),
                    class: "h-7 text-xs"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Plus), { class: "w-3 h-3 mr-1" }),
                      _cache[8] || (_cache[8] = _createTextVNode(" Añadir Periodo ", -1))
                    ]),
                    _: 1
                  }, 8, ["onClick"])
                ])
              ])
            ]))
          }), 128))
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_18, [
        _cache[10] || (_cache[10] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[9] || (_cache[9] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})